/** Vehicle types and the City / Local access rules. */
export const CITY_VEHICLES = ['saloon', 'hatchback', 'suv', 'minivan', 'minibus'] as const;
/** Vehicles of a driver who signed up in Local mode. They work in Local mode only, for good. */
export const LOCAL_KINDS = ['okada', 'keke_napep', 'korope', 'truck', 'car'] as const;
export const VEHICLE_TYPES = [...CITY_VEHICLES, ...LOCAL_KINDS] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

/** What a rider can search for in Local mode. `car` also covers a City vehicle its driver has switched to Local mode. */
export type LocalKind = (typeof LOCAL_KINDS)[number];

export type Range = 'city' | 'local';

export const VEHICLE_LABEL: Record<VehicleType, string> = {
  saloon: 'Saloon car', hatchback: 'Hatchback', suv: 'SUV', minivan: 'Minivan', minibus: 'Minibus',
  okada: 'Okada', keke_napep: 'Keke napep', korope: 'Korope', truck: 'Truck', car: 'Car',
};

export const isLocalOnly = (v: string): boolean => (LOCAL_KINDS as readonly string[]).includes(v);

export interface DriverAccess { vtype: VehicleType; localMode: boolean }
export interface RiderAccess { rg: Range; lv?: LocalKind | undefined }

/** A driver works in exactly one mode at a time. Local-only vehicles are always Local. */
export const driverMode = (d: DriverAccess): Range => (isLocalOnly(d.vtype) || d.localMode ? 'local' : 'city');

export const localKindOf = (vtype: VehicleType): LocalKind => (isLocalOnly(vtype) ? (vtype as LocalKind) : 'car');

/** True when this driver may serve this rider's trip type. */
export function canServe(d: DriverAccess, r: RiderAccess): boolean {
  if (r.rg === 'local') return driverMode(d) === 'local' && localKindOf(d.vtype) === (r.lv ?? 'okada');
  return driverMode(d) === 'city';
}
